/**
 * SettingsLayout — sub-navigation wrapper for `/w/:slug/app/settings/*`.
 *
 * Renders a left sub-nav (200px) grouped by section alongside the active
 * settings page via `<Outlet />`. On mobile (<900px) the sub-nav
 * collapses to a horizontal scrollable tab bar above the content.
 *
 * Items are filtered by the current user's workspace role via
 * `visibleSettings()` from nav-config.ts.
 */

import { useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PageHeader } from '@aigovops/design-system';
import { useWorkspace, type WorkspaceRole } from '../../lib/workspace';
import {
  visibleSettings,
  type NavRole,
  type SettingsNavItem,
} from '../../lib/nav-config';
import { useIsMobile } from './AppShell';

// ---------------------------------------------------------------------------
// Role mapping
// ---------------------------------------------------------------------------

/**
 * Map WorkspaceRole (which includes assessor/advisor) to NavRole for
 * the settings visibility filter. Assessors and advisors see viewer-
 * level items only.
 */
function toNavRole(role: WorkspaceRole): NavRole {
  switch (role) {
    case 'owner':
      return 'owner';
    case 'admin':
      return 'admin';
    case 'editor':
      return 'editor';
    default:
      return 'viewer';
  }
}

// ---------------------------------------------------------------------------
// Section metadata
// ---------------------------------------------------------------------------

const SECTION_ORDER = ['account', 'workspace', 'security', 'audit', 'billing'] as const;

const SECTION_LABELS: Record<string, string> = {
  account: 'Account',
  workspace: 'Workspace',
  security: 'Security',
  audit: 'Audit',
  billing: 'Billing',
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const sectionHeaderStyle: React.CSSProperties = {
  fontSize: 'var(--fs-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--text-muted)',
  padding: '0 var(--sp-2)',
  margin: 0,
};

const itemBaseStyle: React.CSSProperties = {
  display: 'block',
  padding: 'var(--sp-1) var(--sp-2)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--fs-sm)',
  color: 'var(--text-muted)',
  textDecoration: 'none',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  width: '100%',
  textAlign: 'left',
  fontFamily: 'inherit',
  lineHeight: 'inherit',
};

// V98-6 review (Medium): the active item is the DS active-state pair —
// `--accent-soft-text` on `--accent-soft` (C23 holds it to 4.5:1 in light and
// dark); `--accent` on that fill measured 4.42:1.
const itemActiveStyle: React.CSSProperties = {
  ...itemBaseStyle,
  background: 'var(--accent-soft)',
  color: 'var(--accent-soft-text)',
  fontWeight: 500,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SettingsLayout() {
  const { workspace, role } = useWorkspace();
  const navigate = useNavigate();
  const loc = useLocation();
  const isMobile = useIsMobile();

  const prefix = `/w/${workspace.slug}/app/settings`;

  const navRole = toNavRole(role);
  const items = useMemo(() => visibleSettings(navRole), [navRole]);

  // Group items by section, preserving section order
  const grouped = useMemo(() => {
    const bySection = new Map<string, SettingsNavItem[]>();
    for (const item of items) {
      const group = bySection.get(item.section) ?? [];
      group.push(item);
      bySection.set(item.section, group);
    }
    const result: { section: string; label: string; items: SettingsNavItem[] }[] = [];
    for (const key of SECTION_ORDER) {
      const sectionItems = bySection.get(key);
      if (sectionItems && sectionItems.length > 0) {
        result.push({
          section: key,
          label: SECTION_LABELS[key] ?? key,
          items: sectionItems,
        });
      }
    }
    return result;
  }, [items]);

  // Determine active item by matching current path
  const activeHref = useMemo(() => {
    const relPath = loc.pathname.startsWith(prefix + '/')
      ? loc.pathname.slice(prefix.length + 1)
      : loc.pathname === prefix
        ? ''
        : '';
    // Match against item hrefs
    for (const item of items) {
      if (relPath === item.href) return item.href;
    }
    // Fallback: prefix match for nested routes
    for (const item of items) {
      if (relPath.startsWith(item.href + '/')) return item.href;
    }
    return null;
  }, [loc.pathname, prefix, items]);

  // ---------------------------------------------------------------------------
  // Mobile: horizontal scrollable tab bar
  // ---------------------------------------------------------------------------

  if (isMobile) {
    return (
      <div>
        <PageHeader variant="app" title="Settings" />
        <nav
          aria-label="Settings"
          style={{
            display: 'flex',
            gap: 'var(--sp-1)',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            paddingBottom: 'var(--sp-2)',
            marginBottom: 'var(--sp-4)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          {items.map((item) => {
            const href = `${prefix}/${item.href}`;
            const isActive = activeHref === item.href;
            return (
              <a
                key={item.id}
                href={href}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(href);
                }}
                aria-current={isActive ? 'page' : undefined}
                style={{
                  flexShrink: 0,
                  padding: 'var(--sp-1) var(--sp-2)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--fs-sm)',
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  color: isActive ? 'var(--accent-soft-text)' : 'var(--text-muted)',
                  background: isActive ? 'var(--accent-soft)' : 'transparent',
                  fontWeight: isActive ? 500 : 400,
                }}
              >
                {item.label}
              </a>
            );
          })}
        </nav>
        <Outlet />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Desktop: side sub-nav + content
  // ---------------------------------------------------------------------------

  return (
    <div>
      <PageHeader variant="app" title="Settings" />
      <div
        style={{
          display: 'flex',
          gap: 0,
          minHeight: 400,
        }}
      >
        {/* Sub-nav */}
        <nav
          aria-label="Settings"
          style={{
            width: 200,
            flexShrink: 0,
            borderRight: '1px solid var(--border)',
            padding: 'var(--sp-3)',
          }}
        >
          {grouped.map((group, groupIdx) => {
            const sectionId = `settings-section-${group.section}`;
            return (
              <div
                key={group.section}
                role="group"
                aria-labelledby={sectionId}
                style={groupIdx > 0 ? { marginTop: 'var(--sp-4)' } : undefined}
              >
                <h3 id={sectionId} style={sectionHeaderStyle}>{group.label}</h3>
                <div style={{ marginTop: 'var(--sp-1)', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {group.items.map((item) => {
                    const href = `${prefix}/${item.href}`;
                    const isActive = activeHref === item.href;
                    return (
                      <a
                        key={item.id}
                        href={href}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(href);
                        }}
                        aria-current={isActive ? 'page' : undefined}
                        style={isActive ? itemActiveStyle : itemBaseStyle}
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            (e.currentTarget as HTMLElement).style.background = 'var(--surface-alt)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            (e.currentTarget as HTMLElement).style.background = 'transparent';
                          }
                        }}
                      >
                        {item.label}
                      </a>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Content area */}
        <div style={{ flex: 1, minWidth: 0, paddingLeft: 'var(--sp-5)' }}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}
